import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageMeta from '@/components/feature/PageMeta';
import SectionHeader from '@/components/feature/SectionHeader';
import BookSkeletonCard from '@/components/feature/BookSkeletonCard';
import BookCard from '@/components/feature/BookCard';
import { externalLinks } from '@/mocks/siteSettings';
import { books } from '@/mocks/books';
import { useScrollReveal } from '@/hooks/useScrollReveal';

function RevealWrap({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const { ref, isVisible } = useScrollReveal(0.06);
  return (
    <div ref={ref} className={`section-reveal ${isVisible ? 'is-visible' : ''} ${className}`}>
      {children}
    </div>
  );
}

export default function BookList() {
  return (
    <>
      <PageMeta
        title="書籍｜複刻文化 Fuko"
        description="瀏覽複刻文化出版書籍。正式書目資訊請前往崧博出版書籍資料庫查詢。"
        canonical="https://fuko.sonbook.net/books"
        noIndex
      />
      <Navbar />
      <main>

        {/* ═══════════════════════════════════════
            §1 · BOOK CATALOG
        ═══════════════════════════════════════ */}
        <section className="relative overflow-hidden min-h-[60vh]" style={{ backgroundColor: 'oklch(var(--background-100))' }}>
          {/* ── Editorial background ── */}
          <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
            {/* Hero image */}
            <img src="https://readdy.ai/api/search-image?query=Rows%20of%20vintage%20cloth-bound%20books%20in%20muted%20burgundy%20olive%20green%20navy%20and%20warm%20brown%20arranged%20on%20dark%20wooden%20library%20shelves%20with%20elegant%20brass%20library%20ladder%20visible%20on%20one%20side%2C%20soft%20warm%20overhead%20lighting%20creating%20a%20quiet%20scholarly%20atmosphere%20with%20gentle%20shadows%20between%20the%20books%2C%20some%20books%20slightly%20leaning%20creating%20an%20organic%20lived-in%20feel%20rather%20than%20perfect%20alignment%2C%20modern%20minimalist%20editorial%20library%20photography%2C%20intellectual%20academic%20atmosphere%20with%20archival%20quality%2C%20refined%20and%20restrained%20without%20being%20dramatic%20or%20ornate%2C%20fine%20art%20documentary%20style%2C%20deep%20warm%20neutral%20color%20palette%20of%20mahogany%20amber%20deep%20green%20and%20cream%2C%20sophisticated%20cultural%20publishing%20and%20bibliographic%20visual%20identity%2C%20clean%20composition%20with%20balanced%20negative%20space%2C%20editorial%20magazine%20quality%20photograph%20suitable%20for%20a%20book%20catalog%20page%20hero&width=1600&height=900&seq=fuko-books-hero-01&orientation=landscape" alt="" className="absolute inset-0 w-full h-full object-cover" style={{ opacity: 0.28 }} />
            {/* Page number */}
            <span className="absolute top-8 right-[8%] font-mono text-[10px] tracking-[0.35em] hidden md:block" style={{ color: 'oklch(var(--foreground-400)/0.20)' }}>P. 021</span>
            {/* Ghost watermark */}
            <span className="absolute bottom-8 left-[6%] font-heading font-bold select-none hidden lg:block" style={{ fontSize: 120, color: 'oklch(var(--foreground-950)/0.012)' }}>CATALOG</span>
            {/* Second ghost — 複刻 */}
            <span className="absolute top-[10%] right-[5%] font-heading font-bold select-none hidden xl:block" style={{ fontSize: 100, color: 'oklch(var(--foreground-950)/0.014)' }}>複刻</span>
            {/* Top accent strip */}
            <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(to right, transparent 0%, oklch(var(--primary-500)/0.18) 20%, oklch(var(--accent-500)/0.10) 70%, transparent 100%)' }}></div>
          </div>

          <div className="relative w-full px-6 md:px-12 lg:px-16 pt-20 md:pt-24 pb-20 md:pb-28">
            <SectionHeader
              title="書籍"
              sectionNumber="B"
              englishLabel="CATALOG"
            />

            {books.length === 0 ? (
              <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
                {/* Left — info column */}
                <div className="lg:w-[30%]">
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                    完整書名、作者、分類、ISBN 及出版資訊將由崧博出版書籍資料庫匯入。
                    目前暫無可顯示的書籍，請前往書籍資料庫查詢正式書目。
                  </p>
                  <a
                    href={externalLinks.catalog}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 font-medium rounded-sm text-sm whitespace-nowrap transition-colors"
                    style={{ padding: '12px 26px', backgroundColor: 'oklch(var(--primary-500))', color: 'oklch(var(--background-50))' }}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'oklch(var(--primary-600))')}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'oklch(var(--primary-500))')}
                  >
                    前往書籍資料庫<i className="ri-external-link-line text-xs"></i>
                  </a>

                  {/* Book info fields */}
                  <div className="mt-10 space-y-3 pt-6 hidden md:block" style={{ borderTop: '1px solid oklch(var(--foreground-200)/0.30)' }}>
                    {['書名','作者','ISBN','出版日期','分類'].map(f => (
                      <div key={f} className="flex items-center gap-3">
                        <span className="font-mono text-[10px] tracking-[0.2em] w-16 flex-shrink-0" style={{ color: 'oklch(var(--foreground-400)/0.48)' }}>{f}</span>
                        <div className="flex-1 h-px" style={{ backgroundColor: 'oklch(var(--foreground-200)/0.25)' }}></div>
                      </div>
                    ))}
                    <p className="text-[11px] pt-2 font-label" style={{ color: 'oklch(var(--foreground-400)/0.48)' }}>資料由書籍資料庫提供</p>
                  </div>
                </div>

                {/* Right — skeleton cards */}
                <div className="lg:w-[70%]">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <BookSkeletonCard variant="tall" />
                    <BookSkeletonCard variant="wide" />
                    <BookSkeletonCard variant="square" />
                    <BookSkeletonCard variant="tall" />
                  </div>
                  <p className="mt-10 text-center text-xs font-label" style={{ color: 'oklch(var(--foreground-400)/0.52)' }}>
                    正式書籍資料將由書籍資料庫匯入
                  </p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {books.map((book) => (
                  <BookCard key={book.slug} book={book} />
                ))}
              </div>
            )}
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}